
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useFirebaseFunction } from '@/hooks/useFirebaseFunction';
import type { Coordinates } from '@skillscratch/shared-types';
import { useBonusScratches } from '@/hooks/useBonusScratches';
import type { GameStateProviderProps } from './GameStateContext';
import type { GameStatus } from './GameStateContext';
import { GameStateContext } from './GameStateContext';
import { useBonusBombs } from '@/hooks/useBonusBombs';



export function GameStateProvider({ 
  children,
  grid,
  scratchLimit, 
  treasureCount,
  onSquareReveal,
  onGameEnd,
  cardName
}: GameStateProviderProps) {
  const [gameStatus, setGameStatus] = useState<GameStatus>('ready-to-start');
  const [showBoardLoading, setShowBoardLoading] = useState(false);
  const [timerStartTime, setTimerStartTime] = useState(Date.now());

  const [loseStreakResponse, isStreakLoading] = useFirebaseFunction<void, { loseStreak: number }>(
    'getLoseStreak',
    (callable) => callable(undefined),
    []
  );
  const loseStreak = loseStreakResponse?.loseStreak ?? 0;

  const revealedSquareCount = useMemo(
    () => {
      return grid.flat().filter(square => {
        const isRevealed = square.type !== 'hidden' && square.type !== 'blownUp';
        const isEnabled = !('disabled' in square) || !square.disabled;
        
        return isRevealed && isEnabled;
      }).length;
    },
    [grid]
  );

  const treasureFoundCount = useMemo(
    () => grid.flat().filter(s => s.type === 'treasure' && !s.disabled).length,
    [grid]
  );

  const endGame = useCallback(async (reason: "no-scratch" | "no-time" | "found-all-treasures") => {
    setGameStatus(reason);
    setShowBoardLoading(true);
    await onGameEnd(reason);
    setShowBoardLoading(false);
  }, [onGameEnd]);

  const {
    bonusScratches,
    showBonusScratchesModal,
    activateBonusScratches
  } = useBonusScratches({
    scratchesLeft: scratchLimit - revealedSquareCount,
    grid,
    treasureFoundCount,
    cardName,
    loseStreak,
    isStreakLoading,
    endGame,
  });

  const scratchesLeft = scratchLimit - revealedSquareCount + (bonusScratches ?? 0);

  const {
    bonusBombs,
    showBonusBombsModal,
    activateBonusBomb,
    checkForBonusBombs
  } = useBonusBombs({
    scratchesLeft,
    treasureFoundCount,
    loseStreak,
    isStreakLoading
  });

  const handleSquareReveal = useCallback((coordinates: Coordinates) => {
    if (gameStatus === 'ready-to-start') {
      setGameStatus('playing');
      setTimerStartTime(Date.now());      
    }
    onSquareReveal(coordinates);
    checkForBonusBombs();
  }, [gameStatus, onSquareReveal, checkForBonusBombs]);

  const handleActivateBonusScratches = useCallback(() => {
    const newTime = activateBonusScratches();
    setTimerStartTime(time => {
      const currentTime = newTime;
      const elapsedTime = currentTime - time;
      return currentTime - (elapsedTime - 60000);
    });
  }, [activateBonusScratches]);

  const handleActivateBonusBomb = useCallback(async (numberOfSquares: number) => {
    try {
      const newTime = await activateBonusBomb(numberOfSquares);
      
      await new Promise(resolve => setTimeout(resolve, 100));
      
      setTimerStartTime(time => {
        const currentTime = newTime;
        const elapsedTime = currentTime - time;
        return currentTime - (elapsedTime - 30000);
      });
      
      return newTime;
    } catch (error) {
      console.error("Error activating bonus bombs:", error);
      return Date.now();
    }
  }, [activateBonusBomb]);

  useEffect(() => {
    if (treasureFoundCount === treasureCount) {
      endGame('found-all-treasures');
    }
  }, [treasureFoundCount, treasureCount, endGame]);

  const value = useMemo(() => ({
    gameStatus,
    showBoardLoading,
    timerStartTime,
    bonusScratches,
    bonusBombs,
    showBonusBombsModal,
    showBonusScratchesModal,
    scratchesLeft,
    treasureFoundCount,
    handleSquareReveal,
    activateBonusScratches: handleActivateBonusScratches,
    activateBonusBombs: handleActivateBonusBomb,
    onTimeUp: () => endGame('no-time'),
    cardName,
  }), [gameStatus, showBoardLoading, timerStartTime, bonusScratches, bonusBombs, 
    showBonusBombsModal, showBonusScratchesModal, scratchesLeft, treasureFoundCount, 
    handleSquareReveal, handleActivateBonusScratches, handleActivateBonusBomb, cardName, endGame]);

  return (
    <GameStateContext.Provider value={value}>
      {children}
    </GameStateContext.Provider>
  );
}